import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, filter, takeUntil, withLatestFrom } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { NavigationMenuItem } from '@core/interfaces';
import { BroadcastService, ConfirmationService, DeveloperService, EnvironmentService, UpdateService, WindowService } from '@core/services';

import { environment } from '@environment';

import { PERMISSIONS } from '@auth/permissions';
import { AuthService } from '@auth/services';
import { AuthActions } from '@auth/state';
import { CustomerService } from '@customer/services';
import { FacilityService } from '@facility/services';

@Component({
  selector: 'z-navigation',
  templateUrl: './navigation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();

  theme: string;
  mobile: boolean = false;

  items$ = new BehaviorSubject<NavigationMenuItem[]>([]);
  generateMenu$ = new Subject<void>();

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private authService: AuthService,
    private store: Store,
    private translocoService: TranslocoService,
    private facilityService: FacilityService,
    private confirmationService: ConfirmationService,
    private broadcastService: BroadcastService,
    private customerService: CustomerService,
    private updateService: UpdateService,
    private windowService: WindowService,
    private router: Router,
    public developerService: DeveloperService,
    public environmentService: EnvironmentService
  ) {}

  ngOnInit() {
    this.generateMenu$.pipe(debounceTime(10), takeUntil(this.destroy$)).subscribe(() => {
      this.items$.next(this.generate());
    });

    this.authService.permissions$
      .pipe(
        withLatestFrom(this.authService.nameid$),
        filter(([permissions, nameid]) => !!permissions && !!nameid),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.generateMenu$.next();
      });

    // toggle theme switcher menu items
    this.authService.theme$.pipe(takeUntil(this.destroy$)).subscribe((theme) => {
      this.theme = theme;
      this.generateMenu$.next();
    });

    this.translocoService
      .selectTranslation()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.items$.next(this.generate());
      });

    this.windowService.mobile$.pipe(takeUntil(this.destroy$)).subscribe((mobile) => {
      if (mobile) {
        this.mobile = true;
      } else {
        this.mobile = false;
      }

      this.generateMenu$.next();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  home() {
    void this.router.navigate(['', 'home']);
  }

  generate(): NavigationMenuItem[] {
    const items: NavigationMenuItem[] = [];

    //
    // inventory
    //

    if (this.authService.checkPermission(PERMISSIONS.WSINVINQUIRE)) {
      items.push({
        label: this.translate('core.navigation.inventory.root.label'),
        title: this.translate('core.navigation.inventory.root.title'),
        icon: 'far fa-shelves',
        group: 'inventory',
        items: [
          {
            label: this.translate('core.navigation.inventory.inquiry.label'),
            title: this.translate('core.navigation.inventory.inquiry.title'),
            icon: 'far fa-search',
            group: 'inventory',
            routerLink: ['', 'inventory'],
          },
        ],
      });
    }

    //
    // orders
    //

    if (this.authService.checkPermissionAny([PERMISSIONS.WSORDINQUIRE, PERMISSIONS.WSORDCREATE])) {
      const orderItems: NavigationMenuItem[] = [];

      if (this.authService.checkPermission(PERMISSIONS.WSORDINQUIRE)) {
        orderItems.push({
          label: this.translate('core.navigation.orders.inquiry.label'),
          title: this.translate('core.navigation.orders.inquiry.title'),
          icon: 'far fa-search',
          group: 'orders',
          routerLink: ['', 'orders'],
        });
      }

      if (this.authService.checkPermissionAll([PERMISSIONS.WSORDINQUIRE, PERMISSIONS.WSORDCREATE])) {
        orderItems.push({
          separator: true,
        });
      }

      if (this.authService.checkPermission(PERMISSIONS.WSORDCREATE)) {
        orderItems.push({
          label: this.translate('core.navigation.orders.create_inbound.label'),
          title: this.translate('core.navigation.orders.create_inbound.title'),
          icon: 'far fa-truck',
          group: 'orders',
          routerLink: ['', 'orders', 'create', 'inbound'],
        });

        orderItems.push({
          label: this.translate('core.navigation.orders.create_outbound.label'),
          title: this.translate('core.navigation.orders.create_outbound.title'),
          icon: 'far fa-boxes-packing',
          group: 'orders',
          routerLink: ['', 'orders', 'create', 'outbound'],
        });
      }

      items.push({
        label: this.translate('core.navigation.orders.root.label'),
        title: this.translate('core.navigation.orders.root.title'),
        icon: 'far fa-archive',
        group: 'orders',
        items: [...orderItems],
      });
    }

    //
    // reports
    //

    if (this.authService.checkPermission(PERMISSIONS.WSREPORTS)) {
      items.push({
        label: this.translate('core.navigation.reports.root.label'),
        title: this.translate('core.navigation.reports.root.title'),
        icon: 'far fa-memo',
        group: 'reports',
        items: [
          {
            label: this.translate('core.navigation.reports.run_report.label'),
            title: this.translate('core.navigation.reports.run_report.title'),
            icon: 'far fa-rocket-launch',
            group: 'reports',
            routerLink: ['', 'reports'],
          },
          {
            label: this.translate('core.navigation.reports.completed.label'),
            title: this.translate('core.navigation.reports.completed.title'),
            icon: 'far fa-memo-circle-check',
            group: 'reports',
            routerLink: ['', 'reports', 'completed'],
          },
        ],
      });
    }

    //
    // admin
    //

    if (this.authService.checkPermission(PERMISSIONS.WSADMIN)) {
      items.push({
        label: this.translate('core.navigation.admin.root.label'),
        title: this.translate('core.navigation.admin.root.title'),
        icon: 'far fa-screwdriver-wrench',
        group: 'admin',
        items: [
          {
            label: this.translate('core.navigation.admin.users.label'),
            title: this.translate('core.navigation.admin.users.title'),
            icon: 'far fa-user-gear',
            group: 'admin',
            routerLink: ['', 'admin', 'users'],
          },
          {
            label: this.translate('core.navigation.admin.create_user.label'),
            title: this.translate('core.navigation.admin.create_user.title'),
            icon: 'far fa-user-plus',
            group: 'admin',
            routerLink: ['', 'admin', 'users', 'create'],
          },
          {
            separator: true,
          },
          {
            label: this.translate('core.navigation.admin.image_upload.label'),
            title: this.translate('core.navigation.admin.image_upload.title'),
            icon: 'far fa-image',
            group: 'admin',
            routerLink: ['', 'admin', 'image-upload'],
          },
        ],
      });
    }

    //
    // user menu
    //

    const userMenuItems: NavigationMenuItem[] = [];

    if (this.mobile) {
      if (this.authService.allcusts !== 'N') {
        userMenuItems.push({
          label: this.translate('core.navigation.profile.change_customer_mobile.label', { custid: this.authService.custid }),
          title: this.translate('core.navigation.profile.change_customer_mobile.title', { custid: this.authService.custid }),
          icon: 'far fa-buildings',
          command: () => {
            this.customerService.changeCustomer();
          },
          disabled: this.authService.allcusts === 'N',
        });
      }

      if (this.authService.chgFacility !== 'N') {
        userMenuItems.push({
          label: this.translate('core.navigation.profile.change_facility_mobile.label', { facility: this.authService.facility }),
          title: this.translate('core.navigation.profile.change_facility_mobile.title', { facility: this.authService.facility }),
          icon: 'far fa-warehouse-alt',
          command: () => {
            this.facilityService.changeFacility();
          },
          disabled: this.authService.chgFacility === 'N',
        });
      }
    } else {
      if (this.authService.allcusts !== 'N') {
        userMenuItems.push({
          label: this.translate('core.navigation.profile.change_customer.label'),
          title: this.translate('core.navigation.profile.change_customer.title'),
          icon: 'far fa-buildings',
          command: () => {
            this.customerService.changeCustomer();
          },
          disabled: this.authService.allcusts === 'N',
        });
      }

      if (this.authService.chgFacility !== 'N') {
        userMenuItems.push({
          label: this.translate('core.navigation.profile.change_facility.label'),
          title: this.translate('core.navigation.profile.change_facility.title'),
          icon: 'far fa-warehouse-alt',
          command: () => {
            this.facilityService.changeFacility();
          },
          disabled: this.authService.chgFacility === 'N',
        });
      }
    }

    userMenuItems.push({
      label: this.translate('core.navigation.profile.change_language.label'),
      title: this.translate('core.navigation.profile.change_language.title'),
      icon: 'far fa-earth-americas',
      command: () => {
        this.authService.changeLanguageDialog(true);
      },
    });

    userMenuItems.push({
      separator: true,
    });

    userMenuItems.push({
      label: this.translate('core.navigation.profile.light_mode.label'),
      title: this.translate('core.navigation.profile.light_mode.title'),
      icon: 'far fa-sun',
      visible: this.theme !== 'light',
      command: () => {
        this.authService.useLightTheme();
      },
    });

    userMenuItems.push({
      label: this.translate('core.navigation.profile.dark_mode.label'),
      title: this.translate('core.navigation.profile.dark_mode.title'),
      icon: 'far fa-moon-cloud',
      visible: this.theme !== 'dark',
      command: () => {
        this.authService.useDarkTheme();
      },
    });

    // disabled until service workers on the same domain is supported
    // if (environment.production) {
    //   userMenuItems.push({
    //     separator: true,
    //   });

    //   userMenuItems.push({
    //     label: this.translate('core.navigation.profile.check_for_updates.label'),
    //     title: this.translate('core.navigation.profile.check_for_updates.title'),
    //     icon: 'far fa-cloud-arrow-down',
    //     command: () => {
    //       this.updateService.checkForUpdates();
    //     },
    //   });
    // }

    userMenuItems.push({
      separator: true,
    });

    userMenuItems.push({
      label: this.translate('core.navigation.profile.log_out.label'),
      title: this.translate('core.navigation.profile.log_out.title'),
      icon: 'far fa-sign-out',
      command: () => {
        if (this.broadcastService.instances && this.broadcastService.instances.size > 1) {
          this.confirmationService.confirm({
            title: this.translate('auth.logout.multiple_instance_warning.title'),
            message: this.translate('auth.logout.multiple_instance_warning.message', {
              instances: `${this.broadcastService.instances.size}`,
            }),
            icon: 'fad fa-triangle-exclamation',
            acceptIcon: 'far fa-sign-out',
            acceptLabel: this.translate('auth.logout.multiple_instance_warning.logout'),
            rejectLabel: this.translate('auth.logout.multiple_instance_warning.cancel'),
            defaultFocus: 'reject',
            accept: () => {
              this.store.dispatch(AuthActions.Logout());
            },
            acceptButtonStyleClass: 'p-button-warning',
            rejectButtonStyleClass: 'p-button-secondary',
          });
        } else {
          this.store.dispatch(AuthActions.Logout());
        }
      },
    });

    if (this.mobile) {
      items.push({
        separator: true,
      });

      items.push({
        label: this.authService.nameid,
        title: this.authService.nameid,
        icon: 'far fa-user',
        styleClass: 'lg:ml-5 lg:mr-2 nav-menu-last',
        group: 'user',
        state: {
          type: 'username',
        },
      });

      items.push({
        separator: true,
      });

      for (const item of userMenuItems) {
        items.push(item);
      }
    } else {
      items.push({
        label: this.authService.nameid,
        title: this.authService.nameid,
        icon: 'far fa-user',
        styleClass: 'lg:ml-5 lg:mr-2 nav-menu-last',
        group: 'user',
        state: {
          type: 'username',
        },
        items: [...userMenuItems],
      });
    }

    return items;
  }
}
